import { useCallback, useMemo } from 'react';
import uuid from 'uuid/v1';
import { operationsApi } from '../../../../services/api';
import { useAccount } from '../../account-configurations/hooks/useAccount';
import { APP_EVENT_TYPE, useAppEventContext } from '../../app-events-aggregator';
import { CHAT_AGENT_ACTION_TYPE, CHAT_AGENT_MESSAGE_SOURCE } from '../config/chatAgentActionType';
import { CHAT_AGENT_COMPONENT_TYPE } from '../config/chatAgentComponentType';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { MESSAGE_DIRECTION } from '../constants';
import { formatAction } from '../utils/eventActionsUtils';
import { useChatAgentContextService } from './useChatAgentContextService';

export const useChatTools = ({ openChat, closeChat, sendResponseToChatAgent, sendMessageToChat }) => {
    const { push: pushAppEvent } = useAppEventContext();
    const { getContext } = useChatAgentContextService();
    const { id: accountId } = useAccount() || {};
    const sendMessage = useCallback(
        async ({ message }, originalMessage, { triggerName } = {}) => {
            sendMessageToChat({ message, triggerName, shouldPushAppEvent: true });
        },
        [sendMessageToChat]
    );

    const open = useCallback(() => {
        openChat();
        return true;
    }, [openChat]);
    const close = useCallback(() => {
        closeChat();
        return true;
    }, [closeChat]);

    const quickAnswersComponent = useCallback(
        ({ quickAnswers }) => {
            const message = {
                id: uuid(),
                componentData: {
                    value: quickAnswers.map(({ label }) => label),
                    componentName: CHAT_AGENT_COMPONENT_TYPE.QUICK_ANSWERS,
                },
                flows: quickAnswers.map(({ methodName, args }, index) => ({
                    id: index,
                    actions: [{ data: { methodName, args }, type: CHAT_AGENT_ACTION_TYPE.METHOD }],
                })),
            };

            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
        },
        [sendResponseToChatAgent]
    );

    const receiveImagesMessage = useCallback(
        async ({ images }) => {
            const message = {
                id: uuid(),
                componentData: {
                    value: images,
                    componentName: CHAT_AGENT_COMPONENT_TYPE.IMAGE_LIST,
                },
                flows: [
                    {
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                data: {
                                    value: images,
                                    source: CHAT_AGENT_MESSAGE_SOURCE.AGENT,
                                },
                            }),
                        ],
                    },
                ],
            };

            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
            pushAppEvent({ type: APP_EVENT_TYPE.CHAT_DIALOG_MESSAGE, data: message });
        },
        [sendResponseToChatAgent, pushAppEvent]
    );

    const sendImagesMessage = useCallback(
        async ({ images }, originalMessage) => {
            const context = await getContext();
            const messageObject = {
                ...originalMessage,
                componentData: {
                    value: images,
                    componentName: CHAT_AGENT_COMPONENT_TYPE.IMAGE_LIST,
                    direction: MESSAGE_DIRECTION.OUTGOING,
                },
                flows: [
                    {
                        ...context,
                        actions: [
                            formatAction({
                                type: CHAT_AGENT_ACTION_TYPE.MESSAGE,
                                data: {
                                    value: images,
                                    source: CHAT_AGENT_MESSAGE_SOURCE.USER,
                                },
                            }),
                        ],
                    },
                ],
            };

            sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: messageObject, actions: [] });
            pushAppEvent({ type: APP_EVENT_TYPE.CHAT_DIALOG_MESSAGE, data: messageObject });
        },
        [sendResponseToChatAgent, getContext, pushAppEvent]
    );

    const uploadImagesToChat = useCallback(
        async ({ mimeType }) => {
            if (!accountId) {
                throw new Error('Account ID is required');
            }

            // TODO: Support more file types - currently only supports images
            if (!mimeType.startsWith('image/')) {
                throw new Error(`Unsupported MIME type: ${mimeType}. Currently only image files are supported.`);
            }

            return new Promise((resolve, reject) => {
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = mimeType;
                fileInput.style.display = 'none';
                fileInput.multiple = true;

                fileInput.onchange = async event => {
                    const files = Array.from(event.target.files || []);

                    if (files.length === 0) {
                        reject(new Error('No files selected'));
                        return;
                    }

                    try {
                        const uploadPromises = files.map(async file => {
                            const response = await operationsApi.uploadThumbnail(accountId, file);
                            return {
                                file: file.name,
                                url: response.value,
                            };
                        });

                        const results = await Promise.all(uploadPromises);
                        resolve(results);
                    } catch (error) {
                        reject(new Error(`Failed to upload files: ${error.message}`));
                    } finally {
                        document.body.removeChild(fileInput);
                    }
                };

                fileInput.oncancel = () => {
                    reject(new Error('File selection cancelled'));
                    document.body.removeChild(fileInput);
                };

                document.body.appendChild(fileInput);
                fileInput.click();
            });
        },
        [accountId]
    );

    return useMemo(
        () => ({
            sendMessage,
            quickAnswers: quickAnswersComponent,
            showImageListFromChatAgent: receiveImagesMessage,
            sendImagesMessage,
            uploadImagesToChat,
            open,
            close,
        }),
        [sendMessage, quickAnswersComponent, receiveImagesMessage, sendImagesMessage, uploadImagesToChat, open, close]
    );
};
